import { createShapeNormalizer } from '@change-corgi/fcm/normalizers';

import { defineFcm } from '../shared/factory';

const AMOUNT_PLACEHOLDER = '%{amount}';
const PLACEHOLDER_PATTERN = /%\{[^{}]*\}/g;

function placeholdersIn(value: string): string[] {
	return value.match(PLACEHOLDER_PATTERN) ?? [];
}

/** The sheet renders these keys with no replacements, so any placeholder would reach users verbatim. */
function assertNoPlaceholders(value: string): string {
	const found = placeholdersIn(value);
	if (found.length > 0) {
		throw new Error(`unexpected placeholder(s) ${found.join(', ')}; this key takes no replacements`);
	}
	return value;
}

/** Only %{amount} is replaced at render time; a mistyped or extra placeholder would reach users verbatim. */
function assertOnlyAmountPlaceholder(value: string): string {
	const found = placeholdersIn(value);
	if (!found.includes(AMOUNT_PLACEHOLDER)) {
		throw new Error(`chip_in_cta must keep the ${AMOUNT_PLACEHOLDER} placeholder`);
	}
	const unexpected = found.filter((placeholder) => placeholder !== AMOUNT_PLACEHOLDER);
	if (unexpected.length > 0) {
		throw new Error(
			`chip_in_cta has unexpected placeholder(s) ${unexpected.join(', ')}; only ${AMOUNT_PLACEHOLDER} is replaced`,
		);
	}
	return value;
}

const EN_US_COPY = {
	heading: 'Want us to share it with people you can’t reach?',
	body: 'Sharing reaches your circle. Chipping in helps it reach people you don’t know who’d sign, by getting this petition featured across Change.org, emailed to likely supporters, and shown in ads to new people.',
	chip_in_cta: 'Chip in %{amount} to reach more people',
	skip: 'Skip',
};

type Copy = typeof EN_US_COPY;

type Value = {
	enabled?: boolean;
	experimentName?: string;
	copy: Copy;
};

const normalizeRow = createShapeNormalizer({
	type: 'object',
	shape: {
		enabled: { type: 'boolean', optional: true },
		experimentName: { type: 'string', optional: true },
		copy: {
			type: 'object',
			optional: true,
			shape: {
				heading: { type: 'string', normalizer: assertNoPlaceholders },
				body: { type: 'string', normalizer: assertNoPlaceholders },
				chip_in_cta: { type: 'string', normalizer: assertOnlyAmountPlaceholder },
				skip: { type: 'string', normalizer: assertNoPlaceholders },
			},
		},
	},
});

/** A row without copy keeps the same English object, so the merger can tell stored copy from filled-in copy. */
function normalizeValue(value: unknown): Value {
	const { enabled, experimentName, copy } = normalizeRow(value);
	return { enabled, experimentName, copy: copy ?? EN_US_COPY };
}

const DEFAULT_VALUE: Value = { enabled: false, experimentName: '', copy: EN_US_COPY };

function mostSpecific<K extends keyof Value>(key: K, rows: readonly Value[]): Value[K] | undefined {
	return rows.find((row) => row[key] !== undefined)?.[key];
}

export const FCM_REVENUE_SHARE_PROMOTE_SLIDE_UP_EXPERIMENT = defineFcm('revenue_share_promote_slide_up_experiment')
	.asJson(normalizeValue)
	.withDefault(DEFAULT_VALUE)
	.withMerger(({ localeValue, countryValue, globalValue }) => {
		const rows = [localeValue, countryValue, globalValue].filter((row) => row !== DEFAULT_VALUE);
		return {
			enabled: globalValue.enabled === false && globalValue !== DEFAULT_VALUE ? false : mostSpecific('enabled', rows),
			experimentName: mostSpecific('experimentName', rows),
			copy: rows.find((row) => row.copy !== EN_US_COPY)?.copy ?? EN_US_COPY,
		};
	})
	.toConfig({
		owner: 'squad-koi',
		scope: 'corgi-front-end',
		context: 'PSF share page (second share screen)',
		description: `Gates the Second Promote Ask R1 experiment that shows a promote bottom sheet after Continue/Skip on the PSF second
			share screen, and carries the sheet's copy, which the front-end serves as corgi.psf.share.promote_slide_up.* through
			I18nOverrides (docs/packages/experiments/i18n-copy.md). The experiment is rolled out per market: give each launch
			locale (or country) row enabled, experimentName and a copy object with all four keys (the template is that row), and
			leave the global row unset. The kill switch is a global row of exactly { "enabled": false }: it switches the
			experiment off in every market at once, so never save enabled: false on the global row unless that is the intent.
			copy.chip_in_cta must keep the %{amount} placeholder, which is replaced at render time with the localized chip-in
			amount (e.g. "$3"). A row that fails validation is ignored (InvalidFcmError is reported) rather than applied, the
			global kill switch included: that market then resolves from the next row down, and to an unset (disabled) gate with
			English copy when none applies. If the variant wins, move the copy into config/i18n/corgi.en-US.yml under that key
			path (Weblate) before removing this config through the usual experiment lifecycle.`,
		mergeLogic:
			'Each field is resolved independently, preferring locale over country over global among the rows that set it: enabled and experimentName come from the most specific row that sets them (so an unset global row lets each market decide), except that a valid global row with enabled: false wins everywhere as a kill switch. copy comes from the most specific row that stores one; when none does, the English copy in this config is used.',
		supportedGroups: { global: true, locales: true, countries: true },
		template: {
			enabled: true,
			experimentName: 'revenue_share_promote_slide_up_experiment',
			copy: EN_US_COPY,
		},
	});
